'use client';

import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Flex, Icon, Input, Spinner, Text, Textarea } from '@chakra-ui/react';
import { LuStar } from 'react-icons/lu';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

import { api } from '@/services/api';
import { AuthContext } from '@/contexts/AuthContext';
import { toaster } from '@/components/ui/toaster';
import { ResponseError } from '@/types/Error';

interface PublicReview {
  id: string;
  rating: number;
  title: string | null;
  comment: string;
  verified_purchase: boolean;
  created_at: string;
  customer_name: string;
  reply: string | null;
  reply_at: string | null;
}

interface RatingSummary {
  average: number;
  count: number;
}

const Stars = ({
  rating,
  size = 16,
  onHover,
  onClick,
}: {
  rating: number;
  size?: number;
  onHover?: (value: number) => void;
  onClick?: (value: number) => void;
}) => (
  <Flex gap="2px">
    {[1, 2, 3, 4, 5].map((i) => (
      <Icon
        key={i}
        fontSize={`${size}px`}
        color={i <= rating ? '#F5A623' : 'rgba(0,0,0,0.18)'}
        cursor={onClick ? 'pointer' : 'default'}
        onMouseEnter={() => onHover?.(i)}
        onClick={() => onClick?.(i)}
      >
        <LuStar />
      </Icon>
    ))}
  </Flex>
);

const ProductReviews = ({ productId }: { productId: string }) => {
  const { isAuthenticated } = useContext(AuthContext);

  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [summary, setSummary] = useState<RatingSummary>({ average: 0, count: 0 });
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`product-reviews/${productId}`);
      setReviews(response.data.reviews);
      setSummary(response.data.summary);
      setAlreadyReviewed(response.data.already_reviewed);
    } catch {
      // avaliações são um complemento, não bloqueia a página de produto
    } finally {
      setIsLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmit = useCallback(async () => {
    if (!rating || !comment.trim()) return;

    try {
      setIsSubmitting(true);
      await api.post('product-reviews', {
        product_id: productId,
        rating,
        title: title.trim() || null,
        comment: comment.trim(),
      });
      toaster.create({
        type: 'success',
        title: 'Avaliação enviada',
        description: 'Obrigado! A sua avaliação vai ser analisada antes de ser publicada.',
      });
      setRating(0);
      setTitle('');
      setComment('');
      setAlreadyReviewed(true);
    } catch (e) {
      const err = e as ResponseError;
      toaster.create({
        type: 'error',
        title: 'Erro ao enviar avaliação',
        description:
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Tente novamente mais tarde.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, title, comment, productId]);

  if (isLoading) {
    return (
      <Flex py="40px" justify="center">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap="28px" p={{ base: '20px', md: '32px' }}>
      {/* Summary */}
      {summary.count > 0 ? (
        <Flex align="center" gap="14px">
          <Text fontSize="32px" fontWeight="800" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
            {summary.average.toFixed(1)}
          </Text>
          <Flex flexDir="column" gap="2px">
            <Stars rating={Math.round(summary.average)} size={18} />
            <Text fontSize="13px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>
              {summary.count} {summary.count === 1 ? 'avaliação' : 'avaliações'}
            </Text>
          </Flex>
        </Flex>
      ) : (
        <Flex flexDir="column" align="center" gap="10px" py="24px">
          <Stars rating={0} size={28} />
          <Text fontSize="16px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
            Ainda não há avaliações
          </Text>
          <Text
            fontSize="13px"
            color={{ base: 'rgba(0,0,0,0.38)', _dark: 'rgba(255,255,255,0.35)' }}
            textAlign="center"
          >
            Seja o primeiro a partilhar a sua opinião sobre este produto.
          </Text>
        </Flex>
      )}

      {/* Review list */}
      {reviews.length > 0 && (
        <Flex flexDir="column" gap="18px">
          {reviews.map((review) => (
            <Flex
              key={review.id}
              flexDir="column"
              gap="6px"
              pb="18px"
              borderBottom="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
            >
              <Flex align="center" justify="space-between">
                <Stars rating={review.rating} />
                <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.35)', _dark: 'rgba(255,255,255,0.3)' }}>
                  {format(new Date(review.created_at), 'dd/MM/yyyy', { locale: pt })}
                </Text>
              </Flex>
              {review.title && (
                <Text fontWeight="700" fontSize="14px" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                  {review.title}
                </Text>
              )}
              <Text fontSize="14px" color={{ base: 'rgba(0,0,0,0.7)', _dark: 'rgba(255,255,255,0.7)' }}>
                {review.comment}
              </Text>
              <Flex align="center" gap="8px">
                <Text fontSize="12px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
                  {review.customer_name}
                </Text>
                {review.verified_purchase && (
                  <Text fontSize="11px" fontWeight="700" color="#16A34A">
                    Compra Verificada
                  </Text>
                )}
              </Flex>

              {review.reply && (
                <Box
                  mt="8px"
                  ml="16px"
                  p="12px"
                  borderRadius="10px"
                  bg={{ base: 'rgba(0,176,202,0.05)', _dark: 'rgba(0,176,202,0.08)' }}
                  borderLeft="3px solid #00B0CA"
                >
                  <Text fontSize="12px" fontWeight="700" color="#00838F" mb="4px">
                    Resposta da idO
                  </Text>
                  <Text fontSize="13px" color={{ base: 'rgba(0,0,0,0.65)', _dark: 'rgba(255,255,255,0.6)' }}>
                    {review.reply}
                  </Text>
                </Box>
              )}
            </Flex>
          ))}
        </Flex>
      )}

      {/* Submission form */}
      <Box
        pt="20px"
        borderTop="1px solid"
        borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
      >
        {!isAuthenticated ? (
          <Text fontSize="14px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
            Inicie sessão para deixar a sua avaliação sobre este produto.
          </Text>
        ) : alreadyReviewed ? (
          <Text fontSize="14px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
            Já avaliou este produto. Obrigado pela sua opinião!
          </Text>
        ) : (
          <Flex flexDir="column" gap="12px" maxW="420px">
            <Text fontWeight="700" fontSize="15px" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
              Deixe a sua avaliação
            </Text>
            <Box onMouseLeave={() => setHoverRating(0)} w="fit-content">
              <Stars
                rating={hoverRating || rating}
                size={24}
                onHover={setHoverRating}
                onClick={setRating}
              />
            </Box>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título (opcional)"
              fontSize="14px"
            />
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Escreva a sua opinião sobre o produto..."
              fontSize="14px"
              minH="90px"
              resize="none"
            />
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !rating || !comment.trim()}
              h="40px"
              borderRadius="8px"
              bg="#00B0CA"
              color="white"
              fontSize="14px"
              fontWeight="700"
              opacity={isSubmitting || !rating || !comment.trim() ? 0.6 : 1}
              _hover={{ bg: '#0097A7' }}
            >
              {isSubmitting ? <Spinner size="sm" /> : 'Enviar Avaliação'}
            </Button>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default ProductReviews;
